import { useMemo, useState } from 'react';
import { Search, MapPin, ShieldCheck, ShieldAlert, Landmark } from 'lucide-react';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/Table';
import {
  useCapagResumo, useCapagPorUf, useCapagMunicipios,
  type CapagNota, type CapagUf,
} from '@/hooks/useMercadoContratos';
import { Kpi, Painel, Vazio } from './ui';
import { nInt } from './format';
import { ChoroplethBR, NOME_UF } from './charts/ChoroplethBR';

// ------------------------------------------------------------------ cores
/** 4 faixas de % de municípios com boa capacidade (A/B). */
const FAIXAS = [
  { min: 0.75, cor: '#15803d', label: '≥ 75% com boa capacidade' },
  { min: 0.50, cor: '#84cc16', label: '50–75%' },
  { min: 0.25, cor: '#f59e0b', label: '25–50%' },
  { min: 0, cor: '#dc2626', label: '< 25%' },
];
const COR_SEM_DADO = 'rgb(var(--ds-border))';

function corPorPct(pct: number | null): string {
  if (pct == null) return COR_SEM_DADO;
  for (const f of FAIXAS) if (pct >= f.min) return f.cor;
  return FAIXAS[FAIXAS.length - 1].cor;
}

/** Cor do badge por nota CAPAG. */
const NOTA_COR: Record<CapagNota, string> = {
  'A+': '#15803d', A: '#16a34a', 'B+': '#2563eb', B: '#3b82f6', C: '#f59e0b', D: '#dc2626',
};
const NOTAS: CapagNota[] = ['A+', 'A', 'B+', 'B', 'C', 'D'];

function NotaBadge({ nota }: { nota: CapagNota | null }) {
  if (!nota) return <span className="text-ds-text-secondary">—</span>;
  return (
    <span
      className="inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold text-white"
      style={{ backgroundColor: NOTA_COR[nota] }}
    >
      {nota}
    </span>
  );
}

// ------------------------------------------------------------------ componente
export function CapagMercado({ portal }: { portal?: boolean }) {
  const [ufSel, setUfSel] = useState<string>('');
  const [busca, setBusca] = useState('');
  const [q, setQ] = useState('');

  const porUf = useCapagPorUf(portal);
  const resumo = useCapagResumo({ uf: ufSel || undefined }, portal);
  const muns = useCapagMunicipios({ q, limite: 30 }, portal);

  // % boa por UF, p/ colorir o mapa.
  const pctPorUf = useMemo(() => {
    const m = new Map<string, { pct: number | null; d: CapagUf }>();
    for (const u of porUf.data ?? []) {
      m.set(u.uf, { pct: u.avaliados > 0 ? u.boa / u.avaliados : null, d: u });
    }
    return m;
  }, [porUf.data]);

  const r = resumo.data;
  const pctBoa = r && r.avaliados > 0 ? r.boa / r.avaliados : null;
  const pctFraca = r && r.avaliados > 0 ? r.fraca / r.avaliados : null;
  const semDados = (porUf.data ?? []).every((u) => u.avaliados === 0);

  const escopo = ufSel || 'Brasil';
  const inputCls = 'rounded-md border border-ds-border bg-ds-surface px-2 py-1.5 text-sm text-ds-text-primary';

  return (
    <div className="space-y-5">
      <p className="text-sm text-ds-text-secondary">
        Capacidade de Pagamento (CAPAG) dos municípios — Tesouro Nacional (Portaria MF nº 501/2017).
        Clique num estado para ver o detalhe. <span className="text-ds-text-primary">Boa capacidade</span> = notas A/B.
      </p>

      {porUf.isError ? <Vazio texto="Falha ao carregar a CAPAG." />
        : semDados ? <Vazio texto="A base CAPAG ainda não foi populada (job capag-sync). Rode a sincronização do Tesouro para ver o mapa." />
          : (
            <>
              {/* KPIs */}
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <Kpi icon={<Landmark size={13} />} titulo={`Municípios avaliados (${escopo})`} valor={nInt(r?.avaliados)} />
                <Kpi icon={<ShieldCheck size={13} />} titulo="Boa capacidade (A/B)" valor={nInt(r?.boa)} detalhe={pctBoa != null ? `${(pctBoa * 100).toFixed(0)}% do total` : undefined} />
                <Kpi icon={<ShieldAlert size={13} />} titulo="Capacidade fraca (C/D)" valor={nInt(r?.fraca)} detalhe={pctFraca != null ? `${(pctFraca * 100).toFixed(0)}% do total` : undefined} />
                <Kpi icon={<MapPin size={13} />} titulo="Nota predominante" valor={r?.predominante ?? '—'} />
              </div>

              <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
                {/* mapa */}
                <Painel
                  titulo={`🗺️ Mapa CAPAG por estado${ufSel ? ` · ${ufSel}` : ''}`}
                  acao={ufSel ? (
                    <button type="button" onClick={() => setUfSel('')} className="text-xs text-ds-brand hover:underline">
                      ← Brasil
                    </button>
                  ) : undefined}
                >
                  <ChoroplethBR
                    ariaLabel="Mapa CAPAG dos estados do Brasil"
                    selected={ufSel}
                    onSelect={(uf) => setUfSel(uf)}
                    fill={(uf) => corPorPct(pctPorUf.get(uf)?.pct ?? null)}
                    title={(uf) => {
                      const info = pctPorUf.get(uf);
                      const pct = info?.pct ?? null;
                      return `${NOME_UF[uf]} (${uf})\n${info ? `${((pct ?? 0) * 100).toFixed(0)}% com boa capacidade · ${nInt(info.d.avaliados)} municípios` : 'sem dados'}`;
                    }}
                  />
                  {/* legenda */}
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-ds-text-secondary">
                    {FAIXAS.map((f) => (
                      <span key={f.label} className="inline-flex items-center gap-1.5">
                        <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: f.cor }} />
                        {f.label}
                      </span>
                    ))}
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block h-3 w-3 rounded-sm" style={{ backgroundColor: COR_SEM_DADO }} />
                      sem dados
                    </span>
                  </div>
                </Painel>

                {/* distribuição por nota + busca */}
                <div className="space-y-4">
                  <Painel titulo={`Distribuição por nota · ${escopo}`}>
                    {!r || r.avaliados === 0 ? <Vazio texto="Sem municípios avaliados no recorte." /> : (
                      <div className="space-y-2">
                        {/* barra empilhada */}
                        <div className="flex h-3 w-full overflow-hidden rounded">
                          {NOTAS.map((n) => {
                            const v = r.distribuicao[n];
                            if (!v) return null;
                            return <div key={n} style={{ width: `${(v / r.avaliados) * 100}%`, backgroundColor: NOTA_COR[n] }} title={`${n}: ${nInt(v)}`} />;
                          })}
                        </div>
                        <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          {NOTAS.map((n) => (
                            <li key={n} className="flex items-center justify-between">
                              <span className="inline-flex items-center gap-1.5 text-ds-text-secondary">
                                <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: NOTA_COR[n] }} />
                                Nota {n}
                              </span>
                              <span className="tabular-nums text-ds-text-primary">{nInt(r.distribuicao[n])}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </Painel>

                  <Painel titulo="🔎 Buscar município">
                    <form
                      className="flex items-center gap-2"
                      onSubmit={(e) => { e.preventDefault(); setQ(busca.trim()); }}
                    >
                      <div className="flex flex-1 items-center gap-2 rounded-md border border-ds-border bg-ds-surface px-2">
                        <Search size={15} className="text-ds-text-secondary" />
                        <input
                          value={busca}
                          onChange={(e) => setBusca(e.target.value)}
                          placeholder="Nome do município…"
                          className="w-full bg-transparent py-1.5 text-sm text-ds-text-primary outline-none"
                        />
                      </div>
                      <button type="submit" className={`${inputCls} font-medium hover:bg-ds-surface2`}>Buscar</button>
                    </form>

                    {q && (
                      muns.isLoading ? <div className="mt-3"><Vazio texto="Buscando…" /></div>
                        : (muns.data ?? []).length === 0 ? <div className="mt-3"><Vazio texto="Nenhum município encontrado." /></div>
                          : (
                            <div className="mt-3 max-h-64 overflow-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Município</TableHead>
                                    <TableHead>UF</TableHead>
                                    <TableHead className="text-right">CAPAG</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {(muns.data ?? []).map((m) => (
                                    <TableRow key={m.codigo_ibge}>
                                      <TableCell className="text-ds-text-primary">{m.nome || '—'}</TableCell>
                                      <TableCell>{m.uf || '—'}</TableCell>
                                      <TableCell className="text-right"><NotaBadge nota={m.nota} /></TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          )
                    )}
                  </Painel>
                </div>
              </div>
            </>
          )}
    </div>
  );
}

export default CapagMercado;
